import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ErrorHandler,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  mergeMap,
  Observable,
  startWith,
  switchMap,
  tap
} from 'rxjs';
import { EmployeeProfile } from '@coin/shared/org-tree/util';
import { Position } from '@coin/shared/core/util-models';
import { Organisation } from '../../../api/generated';
import { OrgManagementState } from '../../../org-management/state/org-management.state';
import { QuickdrawProfileState } from './state/quickdraw-profile.state';
import { LoadingError } from '../../../org-management/models/loading-error.enum';

@Component({
  selector: 'cosmos-quickdraw-profile',
  templateUrl: './quickdraw-profile.component.html',
  styleUrls: ['./quickdraw-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class QuickdrawProfileComponent {
  private orgManagementState = inject(OrgManagementState);
  private quickdrawProfileState = inject(QuickdrawProfileState);
  private destroyRef = inject(DestroyRef);
  private errorHandler = inject(ErrorHandler);
  router = inject(Router);

  public loadingErrorType = LoadingError;

  @Input() public set employeeId(value: string) {
    this.employeeId$.next(value);
  }

  @Input() public set currentOrganisationId(value: string) {
    this.currentOrganisationId$.next(value);
  }

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public close = new EventEmitter<void>();

  public employeeId$ = new BehaviorSubject<string>(undefined);
  public currentOrganisationId$ = new BehaviorSubject<string>(undefined);

  public employeeDataLoading = this.quickdrawProfileState.employeeProfileLoading;

  private organisationNodesChange$ = toObservable(this.orgManagementState.organisationNodes);
  private employeeProfilesChange$ = toObservable(this.quickdrawProfileState.employeeProfiles);
  private positionsChange$ = toObservable(this.quickdrawProfileState.positions);

  public employeeProfile$: Observable<EmployeeProfile> = this.employeeId$.pipe(
    tap(employeeId => {
      this.currentEmployeeId = employeeId;
    }),
    switchMap(id => {
      const employeeProfile = this.quickdrawProfileState.employeeProfile(id);
      return defer(() =>
        this.employeeProfilesChange$.pipe(
          map(() => employeeProfile()),
          startWith(employeeProfile()),
          distinctUntilChanged()
        )
      );
    })
  );

  public employeeProfileLoadingError = this.quickdrawProfileState.employeeProfileLoadingError;

  public additionalPositions$: Observable<Position[]> = combineLatest([
    this.employeeId$,
    this.currentOrganisationId$
  ]).pipe(
    switchMap(([employeeId, currentOrganisationId]) => {
      const otherPositions = this.quickdrawProfileState.otherEmployeePositions(employeeId, currentOrganisationId);
      return defer(() =>
        this.positionsChange$.pipe(
          map(() => otherPositions()),
          startWith(otherPositions()),
          distinctUntilChanged()
        )
      );
    })
  );
  public currentPosition$: Observable<Position> = combineLatest([this.employeeId$, this.currentOrganisationId$]).pipe(
    switchMap(([employeeId, currentOrganisationId]) => {
      const position = this.quickdrawProfileState.employeePosition(employeeId, currentOrganisationId);
      return defer(() =>
        this.positionsChange$.pipe(
          map(() => position()),
          startWith(position()),
          distinctUntilChanged()
        )
      );
    })
  );
  public currentOrganisation$: Observable<Organisation> = this.currentOrganisationId$.pipe(
    switchMap(id => {
      const organisation = this.orgManagementState.organisation(id);
      return this.organisationNodesChange$.pipe(
        map(() => organisation()),
        startWith(organisation()),
        distinctUntilChanged()
      );
    })
  );

  public showManagement$: Observable<boolean> = combineLatest([this.currentPosition$, this.currentOrganisation$]).pipe(
    map(
      ([currentPosition, currentOrganisation]) =>
        !this.isRootOrganisation(currentOrganisation) || !currentPosition?.isHead
    )
  );

  private currentEmployeeId: string;

  constructor() {
    this.employeeId$
      .pipe(
        filter(id => !!id),
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil -- ngxs compatibility: started loads must finish to reset their loading flags
        takeUntilDestroyed(this.destroyRef),
        mergeMap(id => this.loadEmployeeData(id))
      )
      .subscribe();
    this.currentOrganisationId$
      .pipe(
        filter(id => !!id),
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil -- ngxs compatibility: started loads must finish to reset their loading flags
        takeUntilDestroyed(this.destroyRef),
        mergeMap(id => this.loadOrganisationData(id))
      )
      .subscribe();
  }

  private loadEmployeeData(employeeId: string): Observable<void> {
    // ngxs compatibility: a failed dispatch was only reported, it did not end the caller's stream or the other dispatch
    return merge(
      this.quickdrawProfileState.loadEmployeeProfile(employeeId).pipe(catchError(error => this.reportError(error))),
      this.quickdrawProfileState.loadPositions(employeeId).pipe(catchError(error => this.reportError(error)))
    );
  }

  private reportError(error: unknown): Observable<never> {
    this.errorHandler.handleError(error);
    return EMPTY;
  }

  private loadOrganisationData(organisationId: string): Observable<void> {
    return this.orgManagementState
      .loadOrganisationTree(organisationId)
      .pipe(catchError(error => this.reportError(error)));
  }

  private isRootOrganisation(orgData: Organisation): boolean {
    return !orgData?.parentOrganisationId;
  }

  public reload(): void {
    // eslint-disable-next-line rxjs-angular/prefer-takeuntil -- ngxs compatibility: cancelling would leave the loading flags set
    this.loadEmployeeData(this.currentEmployeeId).subscribe();
  }
}
